import React, { Fragment, useContext } from "react";
import Bulk from "./../Bulk/Bulk";
import Basket from "./../Basket/Basket";
import style from "./../Order.module.scss";
import { Container, Row, Col, Input } from "reactstrap";
import { accountProps, customFetchAssetInfo, customFetchAccountInfo } from "./commons";
import { OrderPriceType, TimeFrame, ProductType, Type, Mode, OrderStatus } from "../../../../utils/Order/Order";
import { Field, Symbol, Account, Numeric, Combobox, TimeForce, PriceType } from "../Field/Field";
import { UserType } from "../../../../utils/Helper";
import { Dispatch } from "../store/store";
import { onChange, onAccountChange } from "../Order";
import QuantityHelper from "../Helper/QuantityHelper";
import Booktype from "../Field/Booktype";

const Equity = ({ maxDecimals, updatePrice, newSymbol, booktypes, platformRole, opsViewAccountName }) => {
  const { dispatch, state, services } = useContext(Dispatch);
  const { order, fields, ui, entities } = state;
  const { editable, errors, readonly, hiddens } = fields;

  const accountInfo = (props, updateOrderValues) =>
    customFetchAccountInfo(
      dispatch,
      services.account,
      {
        ...order,
        CurrentAccount: {
          ...order.CurrentAccount,
          ...props,
        },
      },
      updateOrderValues
    );

  let quantities = [];
  let quantityTitle = <span>Quantity</span>;
  if (ui.type == Type.Default || ui.type == Type.Chained || ui.type == Type.Cross) {
    if (order.CurrentAccount.Day) {
      quantities = order.CurrentAccount.Day.map((day) => {
        return {
          date: day.Date,
          status: day.Status,
          quantity: parseFloat(day.AssignedQty),
        };
      });
    }
    quantityTitle = (
      <Fragment>
        <span>Quantity</span>
        {quantities.length > 0 && order.OrderId > 0 && (
          <QuantityHelper quantities={quantities} name="general">
            <i className="icon-info icons" />
          </QuantityHelper>
        )}
      </Fragment>
    );
  }

  return (
    <div className={style.form}>
      <Container>
        <Row>
          {/* Left */}
          <Col xs={12} lg={6}>
            {/* ##### Symbol ###### */}
            <Field label={"Symbol"} error={errors.AssetTicker}>
              <Symbol
                form={order.AssetClassForm}
                onChange={(v) => onChange(dispatch, v)}
                placeholder={"Symbol"}
                disabled={readonly.AssetTicker || (order.offer && +order.offer.OfferId > 0)}
                invalid={!!errors.AssetTicker}
                value={order.AssetTicker}
                description={order.AssetDescription}
                provider={order.AssetSecurityProvider}
                assetid={order.AssetId}
                attrId={"AssetId"}
                attrTicker={"AssetTicker"}
                attrDescription={"AssetDescription"}
                attrProvider={"AssetSecurityProvider"}
                {...services.symbol}
                assetInfo={() => customFetchAssetInfo(dispatch, services.symbol, order)}
                updatePrice={updatePrice}
              />
            </Field>

            <Row>
              {/* ##### Trade Currency ###### */}
              <Col lg={6}>
                <Field label={"Trade Currency"} error={errors.OrderCurrencySymbol}>
                  <Input
                    type="text"
                    placeholder="Trade Currency"
                    value={order.OrderCurrencySymbol || ""}
                    disabled
                  />
                </Field>
              </Col>
              {/* ##### Settlement Currency ###### */}
              <Col lg={6}>
                <Field label={"Settlement Currency"} error={errors.OrderSettlementCurrencySymbol}>
                  <Combobox
                    value={order.OrderSettlementCurrencyId || 0}
                    items={entities.settlementCurrencies}
                    disabled={readonly.OrderSettlementCurrencySymbol}
                    invalid={!!errors.OrderSettlementCurrencySymbol}
                    onChange={(v) => onChange(dispatch, { OrderSettlementCurrencyId: v })}
                    emptyLabel={"Settlement Currency"}
                  />
                </Field>
              </Col>
            </Row>

            {/* ##### Quantity ###### */}
            <Field label={quantityTitle} error={errors.OrderQuantity}>
              <Numeric
                value={order.OrderQuantity}
                placeholder={"Quantity"}
                dataTypeDecimalName="quantity"
                disabled={readonly.OrderQuantity || ui.isBulk || ui.isBasket}
                onChange={(v) => onAccountChange(dispatch, { Quantity: v })}
                invalid={!!errors.OrderQuantity}
              />
            </Field>

            {/* ##### Price Type ###### */}
            <PriceType
              type={order.OrderType}
              form={ProductType.EQUITY}
              currency={order.OrderCurrencySymbol}
              values={{
                limPrice: order.OrderPrice,
                stopPrice: order.OrderStopPrice,
              }}
              errors={{
                type: errors.OrderType,
                limPrice: errors.OrderPrice,
                stopPrice: errors.OrderStopPrice,
              }}
              oldValues={{
                limPrice: order.HasOriginalOrReplacingData? order.OriginalOrReplacingData.OrderPrice : order.OrderPrice,
                stopPrice: order.HasOriginalOrReplacingData? order.OriginalOrReplacingData.OrderStopPrice : order.OrderStopPrice,
              }}
              editable={{
                limPrice: editable.OrderPrice && (order.OrderStatus === OrderStatus.ACKED),
                stopPrice: editable.OrderStopPrice && (order.OrderStatus === OrderStatus.ACKED),
              }}
              readonly={{
                type: readonly.OrderType,
                limPrice: readonly.OrderPrice,
                stopPrice: readonly.OrderStopPrice,
              }}
              onChange={(v) => onChange(dispatch, v)}
              newSymbol={newSymbol}
              maxDecimals={maxDecimals}
            />

            {/* ##### TimeForce ###### */}
            <TimeForce
              date={order.OrderGTD}
              form={ProductType.EQUITY}
              timeForce={order.OrderTimeInForce}
              errors={{
                date: errors.OrderGTD,
                timeForce: errors.OrderTimeInForce,
              }}
              readonly={{
                date: readonly.OrderGTD,
                timeForce: readonly.OrderTimeInForce,
              }}
              onChange={(v) => onChange(dispatch, v)}
            />
            {(ui.userRole === "Trader" || (ui.userRole !== "Trader" && ui.mode !== Mode.EDIT)) && <Booktype
              booktypes={booktypes}
              booktype={order.BookType}
              balancetype={order.CurrentAccount.BalanceType}
              errors={{
                BookType: errors.BookType,
                BalanceType: errors.BalanceType
              }}
              readonly={{
                BookType: readonly.BookType,
                BalanceType: errors.BalanceType
              }}
              onChange={(v) => onChange(dispatch, v)}
              onAccountChange={(v) => onAccountChange(dispatch, v)}
            />}
          </Col>

          {/* Right */}
          <Col xs={12} lg={6}>
            {/* ##### Account ###### */}
            {!ui.isBulk && !ui.isBasket && (
              <Account
                userType={ui.userRole}
                platformRole={platformRole}
                onChange={(v) => onAccountChange(dispatch, v)}
                balanceReady={!!order.AssetId}
                dispatch={dispatch}
                {...services.account}
                accountInfo={accountInfo}
                {...accountProps({ order, readonly, errors })}
                hiddens={{
                  Quantity: true,
                  ...hiddens,
                }}
                opsViewAccountName={opsViewAccountName}
              />
            )}

            {ui.isBulk && (
              <Bulk
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                hiddens={hiddens}
              />
            )}

            {ui.isBasket && (
              <Basket
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                hiddens={hiddens}
              />
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export const defaultValues = () => {
  return {
    OrderSide: "Buy",
    BookType: "Self"
  };
};

export const validations = (order, userType) => {
  const filteredBankAccounts = order.CurrentAccount.clientBankAccounts
  ? order.CurrentAccount.clientBankAccounts.filter(
      (account) => account.currencyISO === order.OrderCurrencySymbol
    )
  : [];

  const isMultiAccounts = order.OrderHasMultipleAccounts;

  return {
    OrderQuantity: { empty: !isMultiAccounts },
    AssetTicker: { empty: true, autosuggestId: "AssetId" },
    OrderType: { empty: true },
    OrderPrice: {
      empty: order.OrderType == OrderPriceType.LIMIT || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderStopPrice: {
      empty: order.OrderType == OrderPriceType.STOP || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderTimeInForce: { empty: true },
    OrderGTD: {
      empty: order.OrderTimeInForce == TimeFrame.GTD,
    },
    OrderSettlementCurrencySymbol: { empty: true },

    // The following properties are from an account.
    AccountName: {
      empty: true,
      accountLevel: true,
    },
    clientBankAccounts: {
      noBankAccountSelected: filteredBankAccounts && filteredBankAccounts.length > 0 && (order.CurrentAccount.clientBankAccount == null || order.CurrentAccount.clientBankAccount == "") && !order.CurrentAccount.useCashAccount,
    },
    HolderName: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    Quantity: {
      empty: true,
      accountLevel: true,
    },
    Instruction: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    MarkupType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    CommissionType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
  };
};

export default Equity;
